import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaSave, FaArrowLeft } from "react-icons/fa";
import { TbCategory2 } from "react-icons/tb";
import { toast } from "react-toastify";
import { updateFeatures, featuresRecord, clearState } from "../../redux/Slices/FeaturesSlice";
import { validateFieldLanguage } from "../../Utils/languageValidation";

const EditFeature = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    title_ar: "",
    title_en: "",
    status: "",
    logo: "",
  });

  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { isLoading: loading, error, success, successMessage, record } = useSelector((state) => state.features);

  useEffect(() => {
    dispatch(clearState());
    dispatch(featuresRecord(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (record) {
      const original = {
        title_ar: record?.title_ar || "",
        title_en: record?.title_en || "",
        logo: record?.logo || null,
        status: record?.status === "active" ? 1 : 0
      };
      setOriginalData(original);
      setFormData(original);
    }
  }, [record]);

  useEffect(() => {
    if (originalData) {
      const hasFormChanges = 
        formData.title_ar !== originalData.title_ar ||
        formData.title_en !== originalData.title_en ||
        formData.status !== originalData.status ||
        formData.logo !== originalData.logo;
      
      setHasChanges(hasFormChanges);
    }
  }, [formData, originalData]);

  useEffect(() => {
    if (success || successMessage) {
      const message = successMessage || "تم التعديل بنجاح";
      toast.success(message, { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
      navigate("/features");
    }
    if (error && error !== null) {
      const errorMessage = typeof error === 'string' ? error : 
                          error?.message || 
                          error?.error || 
                          'حدث خطأ في التعديل';
      toast.error(errorMessage, { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
    }
  }, [success, successMessage, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast.error("الملف يجب أن يكون صورة (jpg, jpeg, png)");
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({ ...prev, [e.target.name]: file }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();


    // BUG-11 fix: التحقق من لغة الحقول قبل الإرسال
    const arError = validateFieldLanguage(formData.title_ar, "ar");
    if (arError) return toast.error(arError);
    const enError = validateFieldLanguage(formData.title_en, "en");
    if (enError) return toast.error(enError);

    dispatch(clearState());

    const data = new FormData();

    if (formData.title_ar !== record?.title_ar) {
      data.append("title_ar", formData.title_ar);
    }
    
    if (formData.title_en !== record?.title_en) {
      data.append("title_en", formData.title_en);
    }
    
    if (formData.status !== (record?.status === "active" ? 1 : 0)) {
      data.append("status", formData.status);
    }

    if (formData.logo && formData.logo !== record?.logo) {
      data.append("logo", formData.logo);
    }

    dispatch(updateFeatures({ id, data }));
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: "right"
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--warning-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(251, 191, 36, 0.15)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div className="container-fluid" style={{ direction: "rtl", padding: "4px 0" }}>
      <style>{`
        .feat-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .feat-custom-select:focus {
          border-color: var(--warning-color) !important;
          box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.15) !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            <TbCategory2 style={{ color: "var(--gray-color)" }} /> تعديل الميزة
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل بيانات أو لوجو الميزة الحالية للمتجر</p>
        </div>
        <Link to="/features" style={{ textDecoration: "none" }}>
          <button style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 18px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            <FaArrowLeft size={11} /> العودة
          </button>
        </Link>
      </div>

      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>العنوان (عربي)</label>
              <input
                type="text"
                name="title_ar"
                value={formData.title_ar}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل العنوان بالعربية..."
              />
            </div>

            <div>
              <label style={labelStyle}>العنوان (إنجليزي)</label>
              <input
                type="text"
                name="title_en"
                value={formData.title_en}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="Enter title in English..."
                dir="ltr"
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>تحديث لوجو الميزة (اختياري)</label>
              <input
                type="file"
                name="logo"
                onChange={handleFileChange}
                style={{ ...inputStyle, padding: "8px 12px" }}
              />
              {record?.logo && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>الصورة الحالية:</span>
                  <img 
                    src={record.logo} 
                    alt="Current logo" 
                    style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, border: "1.5px solid #e2e8f0" }}
                  />
                </div>
              )}
            </div>

            <div>
              <label style={labelStyle}>الحالة</label>
              <select
                className="feat-custom-select"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="" disabled>اختر الحالة...</option>
                <option value="1">🟢 مفعل</option>
                <option value="0">🔴 غير مفعل</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
            <button
              type="button"
              onClick={() => navigate("/features")}
              style={{ padding: "10px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading || !hasChanges}
              style={{
                padding: "10px 28px",
                borderRadius: 10,
                border: "none",
                background: loading ? "var(--warning-color)" : !hasChanges ? "#cbd5e1" : "linear-gradient(135deg,#f59e0b,var(--warning-color))",
                color: "#fff",
                fontWeight: 700,
                cursor: (loading || !hasChanges) ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  جاري الحفظ...
                </>
              ) : (
                <>
                  <FaSave size={12} />
                  تعديل الميزة
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFeature;
